import {
	collection,
	documentId,
	getDocs,
	query,
	serverTimestamp,
	where,
} from 'firebase/firestore';

import {differenceInHours, fromUnixTime} from 'date-fns';
import {db} from '../firebase';
import {getDebug} from './GeneralUse';
import {
	createSingleUser,
	readFirestoreUser,
	updateFirestoreUserField,
} from './UsersCollection';
import {useFirebaseAuthUserStore} from '../stores/FirebaseAuthUserStore';
import {useFirestoreUserStore} from '../stores/FirestoreUserStore';

//
// ****************************************************************************************************
// users/{documentId} === firebaseAuthUser.uid
//
// Harness the power of the UsersCollection.ts to perform higher level activities  for the
// current user with regard to Firebase Authentication.
//
// METHODS
// decrementPostCount
// hoursSinceLastPost
// hoursSinceLastUsernameUpdate
// incrementPostCount
// incrementSigninCount
// setLastPostDate
// syncAppAndFirestore
// syncZustandFirestoreUser
// updateUsername
// userExistsInFirestore
//
const DEBUG = getDebug('auth');
// ****************************************************************************************************
//

/*
// By one.
*/
export const decrementPostCount = async () => {
	DEBUG && console.trace(`decrementPostCount()`);

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		const post_count: number = firestoreUser?.post_count;

		if (firebaseAuthUser && firestoreUser && post_count > 0) {
			await updateFirestoreUserField(
				firebaseAuthUser?.uid,
				'post_count',
				post_count - 1,
			);
			firestoreUser.post_count = post_count - 1;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
//
*/
export const hoursSinceLastPost = () => {
	DEBUG && console.trace(`hoursSinceLastPost()`);

	try {
		// Zustand
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		if (firestoreUser?.last_posted_at) {
			const lastPostedAt = fromUnixTime(
				firestoreUser?.last_posted_at.seconds,
			);
			const now = fromUnixTime(Math.floor(Date.now() / 1000));

			const result = differenceInHours(now, lastPostedAt);

			DEBUG && console.log(`Hours since last Post: ${result}`);
			return result;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	return NaN;
};

/*
//
*/
export const hoursSinceLastUsernameUpdate = () => {
	DEBUG && console.trace(`hoursSinceLastUsernameUpdate()`);

	try {
		// Zustand
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		if (firestoreUser?.username_last_edited_at) {
			const usernameLastEditedAt = fromUnixTime(
				firestoreUser?.username_last_edited_at.seconds,
			);
			const now = fromUnixTime(Math.floor(Date.now() / 1000));

			const result = differenceInHours(now, usernameLastEditedAt);

			DEBUG && console.log(`Hours since last username update: ${result}`);
			return result;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	return NaN;
};

/*
// By one.
*/
export const incrementPostCount = async () => {
	DEBUG && console.trace(`incrementPostCount()`);

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		const post_count: number = firestoreUser?.post_count;

		if (firebaseAuthUser && firestoreUser) {
			await updateFirestoreUserField(
				firebaseAuthUser?.uid,
				'post_count',
				post_count + 1,
			);
			firestoreUser.post_count = post_count + 1;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// By one.
*/
export const incrementSigninCount = async () => {
	DEBUG && console.trace(`incrementSigninCount()`);

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		const site_visit_count: number = firestoreUser?.site_visit_count;

		if (firebaseAuthUser && firestoreUser) {
			await updateFirestoreUserField(
				firebaseAuthUser.uid,
				'site_visit_count',
				site_visit_count + 1,
			);
			firestoreUser.site_visit_count = site_visit_count + 1;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// This will always set the last_posted_at field to NOW (timestamp).
*/
export const setLastPostDate = async () => {
	DEBUG && console.trace(`setLastPostDate()`);

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;
		const firestoreUser = useFirestoreUserStore.getState().firestoreUser;

		if (firebaseAuthUser && firestoreUser) {
			await updateFirestoreUserField(
				firebaseAuthUser.uid,
				'last_posted_at',
				serverTimestamp(),
			);
			syncZustandFirestoreUser();
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// Synchronise whirlwind with the user's Firestore users collection document by:
// 1. Checking if the Google (i.e. Firebase Auth) user exists in the users collection in Firestore, and if not, creating them.
// 2. Synchronising Zustand firestoreUser with live Firestore user.
//
// Currently returns true if operation completed or false if not.
*/
export const syncAppAndFirestore = async () => {
	DEBUG && console.trace(`syncAppAndFirestore()`);

	let synchronised = false;

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;

		const googleUserExistsInFirestore = await userExistsInFirestore();

		// How about a boolean for createSingleUser? syncAppAndFirestore really does two things that we should "evaluate"...
		if (!googleUserExistsInFirestore && firebaseAuthUser) {
			await createSingleUser(firebaseAuthUser.uid);
		}

		synchronised = await syncZustandFirestoreUser();
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG && console.log(`Synchronised app and Firestore`, synchronised);
	return synchronised;
};

/*
// Synchronise the Zustand firestoreUser with live Firestore user.
//
// Currently returns true if operation completed or false if not.
*/
export const syncZustandFirestoreUser = async () => {
	DEBUG && console.trace(`syncZustandFirestoreUser()`);

	let synchronised = false;

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;

		if (firebaseAuthUser) {
			const firestoreUserLive = await readFirestoreUser(
				firebaseAuthUser?.uid,
			);

			const setFirestoreUser =
				useFirestoreUserStore.getState().setFirestoreUser;
			setFirestoreUser(firestoreUserLive);

			synchronised = true;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG &&
		console.log(
			`Synchronised Zustand firestoreUser with live Firestore user`,
			synchronised,
		);
	return true;
};

/*
//
*/
export const updateUsername = async (newUsername: string) => {
	DEBUG && console.trace(`updateUsername()`);

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;

		if (firebaseAuthUser) {
			await updateFirestoreUserField(
				firebaseAuthUser.uid,
				'username',
				newUsername === '' ? null : newUsername,
			);
			await updateFirestoreUserField(
				firebaseAuthUser.uid,
				'username_last_edited_at',
				serverTimestamp(),
			);
		}
	} catch (error) {
		DEBUG && console.error(error);
	}
};

/*
// Signing up or signing in?
//
// Currently returns true if operation completed or false if not.
*/
export const userExistsInFirestore = async () => {
	DEBUG && console.trace(`userExistsInFirestore()`);

	let userExists = false;

	try {
		// Zustand
		const firebaseAuthUser =
			useFirebaseAuthUserStore.getState().firebaseAuthUser;

		if (firebaseAuthUser) {
			const usersCollection = collection(db, 'users');
			const q = query(
				usersCollection,
				where(documentId(), '==', firebaseAuthUser.uid),
			);
			const qSnap = await getDocs(q);

			userExists = qSnap.docs.at(0)?.exists ? true : false;
		}
	} catch (error) {
		DEBUG && console.error(error);
	}

	DEBUG &&
		console.log(
			`Current Firebase Auth user has a corresponding document in the Firestore users collection`,
			userExists,
		);
	return userExists;
};
